import { HoverPopover } from '@app/components/base/HoverPopover';
import { ContentAiIcon } from '@app/modules/content-ai/ContentAiIcon';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { webApi } from '@mi-front/api-clients';
import { theme as appTheme } from '@mi-front/ui';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

const COMPLETED = 'COMPLETED';

const DISPLAY_STATUSES = [COMPLETED, 'SCHEDULED', 'FAILED'] as const;

/** Platform statuses shown when using publishedPlatforms (only COMPLETED and FAILED). */
const PUBLISHED_PLATFORM_DISPLAY_STATUSES = [COMPLETED, 'FAILED'] as const;

const DEFAULT_MAX_VISIBLE_ITEMS = 4;

/** Icon sizes in px: pill (inline) and popover (expanded). */
const ICON_SIZES = {
  small: { pill: 16, popover: 24 },
  medium: { pill: 24, popover: 32 },
} as const;

export type FragmentCardStatusesSize = 'small' | 'medium';

/** Unified platform status item from either uploadStatuses or publishedPlatforms. */
type PlatformStatusItem = { id: string; platform: string; status: string };

type StatusItem =
  | { type: 'upload'; item: PlatformStatusItem }
  | { type: 'external' }
  | { type: 'contentAi' };

const SocialIconWrapper = ({
  children,
  size = 24,
}: {
  children: React.ReactNode;
  size?: number;
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': { fontSize: size * 0.9 },
    }}
  >
    {children}
  </Box>
);

export type FragmentCardStatusesProps = {
  uploadStatuses: webApi.UploadStatusItemDto[];
  externalDelivery?: webApi.ExternalDeliveryStatusDto;
  /** When non-empty, used instead of uploadStatuses for platform icons. */
  publishedPlatforms?: webApi.PublishedPlatformEntryDto[];
  /** When set, shows external delivery icon (takes precedence over externalDelivery.enabled). */
  externalDeliveryStartedAt?: string;
  /** When set, shows Content AI created icon. */
  contentAiCreatedAt?: string;
  /** Icon size: small (default) or medium for bigger icons. */
  size?: FragmentCardStatusesSize;
  /** Max items shown inline before "+N" and tooltip. Default 4. Ignored when wrap is true. */
  maxVisibleItems?: number;
  /** Show unpublished status. Default false. */
  showUnpublished?: boolean;
};

/**
 * Builds platform status items from either publishedPlatforms (preferred when non-empty)
 * or uploadStatuses for display.
 */
function getDisplayPlatformItems(props: {
  uploadStatuses: webApi.UploadStatusItemDto[];
  publishedPlatforms?: webApi.PublishedPlatformEntryDto[];
}): PlatformStatusItem[] {
  const { uploadStatuses, publishedPlatforms } = props;
  if (publishedPlatforms?.length) {
    return publishedPlatforms
      .filter((p) =>
        PUBLISHED_PLATFORM_DISPLAY_STATUSES.includes(
          p.status as (typeof PUBLISHED_PLATFORM_DISPLAY_STATUSES)[number],
        ),
      )
      .map((p) => ({ id: p.uploadId, platform: p.platform, status: p.status }));
  }
  return uploadStatuses
    .filter((u) => DISPLAY_STATUSES.includes(u.status as (typeof DISPLAY_STATUSES)[number]))
    .map((u) => ({ id: u.id, platform: u.platform, status: u.status }));
}

/**
 * Returns true when FragmentCardStatuses would render something (status icons,
 * external delivery icon, Content AI icon, or "Sin publicar" text). Use to avoid wrapping in a
 * pill when there is nothing to show.
 */
export function hasStatusesContent(props: {
  uploadStatuses: webApi.UploadStatusItemDto[];
  externalDelivery?: webApi.ExternalDeliveryStatusDto;
  publishedPlatforms?: webApi.PublishedPlatformEntryDto[];
  externalDeliveryStartedAt?: string;
  contentAiCreatedAt?: string;
  showUnpublished?: boolean;
}): boolean {
  const {
    uploadStatuses,
    externalDelivery,
    publishedPlatforms,
    externalDeliveryStartedAt,
    contentAiCreatedAt,
    showUnpublished = false,
  } = props;
  const displayPlatformItems = getDisplayPlatformItems({ uploadStatuses, publishedPlatforms });
  const showExternalDelivery =
    externalDeliveryStartedAt != null || externalDelivery?.enabled === true;
  const showContentAi = contentAiCreatedAt != null;
  return (
    displayPlatformItems.length > 0 ||
    showExternalDelivery ||
    showContentAi ||
    (showUnpublished &&
      displayPlatformItems.length === 0 &&
      !showExternalDelivery &&
      !showContentAi)
  );
}

/**
 * Renders platform icons for fragment upload statuses and external delivery.
 * Completed uploads use green, SCHEDULED/FAILED use orange. Only COMPLETED, SCHEDULED
 * and FAILED statuses are shown. When external delivery is enabled, shows a green
 * upload icon. For grid overlay, wrap in FragmentMediaPill at the call site.
 */
export function FragmentCardStatuses({
  uploadStatuses,
  externalDelivery,
  publishedPlatforms,
  externalDeliveryStartedAt,
  contentAiCreatedAt,
  size: sizeProp = 'small',
  maxVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  showUnpublished = false,
}: FragmentCardStatusesProps) {
  const { formatMessage } = useIntl();
  const displayPlatformItems = getDisplayPlatformItems({ uploadStatuses, publishedPlatforms });
  const showExternalDelivery =
    externalDeliveryStartedAt != null || externalDelivery?.enabled === true;
  const showContentAi = contentAiCreatedAt != null;

  if (
    displayPlatformItems.length === 0 &&
    !showExternalDelivery &&
    !showContentAi &&
    showUnpublished
  ) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {formatMessage({
          id: 'fragments.listScreen.unpublished',
          defaultMessage: 'Sin publicar',
        })}
      </Typography>
    );
  }

  const iconSizes = ICON_SIZES[sizeProp];
  const allItems: StatusItem[] = [
    ...displayPlatformItems.map((item) => ({ type: 'upload' as const, item })),
    ...(showExternalDelivery ? [{ type: 'external' as const }] : []),
    ...(showContentAi ? [{ type: 'contentAi' as const }] : []),
  ];
  const visibleItems = allItems.slice(0, maxVisibleItems);
  const remainingCount = allItems.length - maxVisibleItems;

  const statusContent = (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.25}>
      {visibleItems.map((statusItem) => {
        if (statusItem.type === 'external') {
          return (
            <Box key="external">
              <SocialIconWrapper size={iconSizes.pill}>
                <FileUploadIcon sx={{ color: appTheme.greenDark }} />
              </SocialIconWrapper>
            </Box>
          );
        }
        if (statusItem.type === 'contentAi') {
          return (
            <Box key="contentAi">
              <SocialIconWrapper size={iconSizes.pill}>
                <ContentAiIcon sx={{ color: appTheme.greenDark }} />
              </SocialIconWrapper>
            </Box>
          );
        }

        try {
          const platform = socialPlatforms.get(statusItem.item.platform);
          const isCompleted = statusItem.item.status === COMPLETED;
          const iconColor = isCompleted ? appTheme.greenDark : appTheme.red;
          return (
            <Box key={statusItem.item.id}>
              <SocialIconWrapper size={iconSizes.pill}>
                {platform.Icon({
                  sx: { color: iconColor },
                })}
              </SocialIconWrapper>
            </Box>
          );
        } catch {
          return null;
        }
      })}

      {remainingCount > 0 && (
        <Typography component="span" variant="caption" sx={{ lineHeight: 1 }} color="#ddd">
          +{remainingCount}
        </Typography>
      )}
    </Box>
  );

  const popoverContent = (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
      {displayPlatformItems.map((item) => {
        try {
          const platform = socialPlatforms.get(item.platform);
          const isCompleted = item.status === COMPLETED;
          const iconColor = isCompleted ? appTheme.greenDark : appTheme.orange;
          return (
            <Box key={item.id}>
              <SocialIconWrapper size={iconSizes.popover}>
                {platform.Icon({
                  sx: { color: iconColor },
                })}
              </SocialIconWrapper>
            </Box>
          );
        } catch {
          return null;
        }
      })}
      {showExternalDelivery && (
        <Box>
          <SocialIconWrapper size={iconSizes.popover}>
            <FileUploadIcon sx={{ color: appTheme.greenDark }} />
          </SocialIconWrapper>
        </Box>
      )}
      {showContentAi && (
        <Box>
          <SocialIconWrapper size={iconSizes.popover}>
            <ContentAiIcon sx={{ color: appTheme.greenDark }} />
          </SocialIconWrapper>
        </Box>
      )}
    </Box>
  );

  const isTruncated = remainingCount > 0;

  if (isTruncated) {
    return <HoverPopover trigger={statusContent}>{popoverContent}</HoverPopover>;
  }

  return statusContent;
}
